import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { SlipGaji } from "../components/SlipGaji";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../api";

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v + "T00:00:00");
  return isNaN(d) ? v : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

export default function GajiPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);

  const load = () => {
    api.get("/gaji/antrean").then(({ data }) => setItems(data)).catch(() => setItems([]));
  };
  useEffect(load, []);

  const markProcessed = async (item) => {
    try {
      await api.put(`/gaji/antrean/${item.id}`);
      toast.success(`Antrean gaji ${item.ID_Trip} (${item.Nama_Supir}) ditandai diproses`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="gaji-page" className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Penggajian Supir</h1>
        <p className="text-sm text-zinc-500 mt-1">Slip gaji bulanan (Gaji_Bulanan_Supir) + antrean trip "Selesai" dengan tanda potongan klaim susut</p>
      </div>

      <SlipGaji canWrite={user?.role === "owner"} />

      <div className="space-y-2">
        <h2 className="text-lg font-semibold tracking-tight text-zinc-900">Antrean Trip Selesai</h2>
      </div>
      <div className="bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden">
        {items === null ? (
          <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <p data-testid="gaji-empty" className="px-5 py-14 text-center text-sm text-zinc-400">
            Antrean kosong. Trip yang diubah ke status "Selesai" akan masuk otomatis.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="gaji-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["ID Trip", "Supir", "Plat Truk", "Tgl Muat", "Susut (KG)", "Potongan Klaim", "Status", "Aksi"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((g) => (
                  <tr key={g.id} data-testid={`gaji-row-${g.ID_Trip}`}
                    className={`border-b border-zinc-100 transition-colors duration-200 ${g.Potongan_Klaim ? "bg-red-50 hover:bg-red-100" : "hover:bg-zinc-50"}`}>
                    <td className="px-4 py-3 font-medium text-zinc-900 whitespace-nowrap">{g.ID_Trip}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{g.Nama_Supir}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{g.Plat_Truk}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(g.Tanggal_Muat)}</td>
                    <td className="px-4 py-3 tabular-nums whitespace-nowrap text-right">{g.Penyusutan_KG == null ? "-" : Number(g.Penyusutan_KG).toLocaleString("id-ID")}</td>
                    <td className="px-4 py-3 max-w-[300px]">
                      {g.Potongan_Klaim ? (
                        <div data-testid={`potongan-flag-${g.ID_Trip}`} className="flex items-start gap-1.5 text-red-700">
                          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                          <span className="text-xs leading-snug">{g.Catatan_Potongan}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-zinc-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge data-testid={`gaji-status-${g.ID_Trip}`} className={g.Status === "Menunggu" ? "bg-amber-100 text-amber-800 border border-amber-200 font-medium" : "bg-emerald-100 text-emerald-800 border border-emerald-200 font-medium"}>
                        {g.Status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {g.Status === "Menunggu" && (
                        <Button data-testid={`proses-gaji-${g.ID_Trip}`} size="sm" variant="outline" onClick={() => markProcessed(g)}
                          className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 transition-colors duration-200">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Tandai Diproses
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
